import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Block,
  Button,
  Icon,
  Row,
  Col,
} from "../../../components/Component";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";
import { useHistory } from "react-router-dom";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import {
  TC_ADMIN_PENDING_STALE_DAYS,
  selectPartnersPendingTooLong,
  selectPaidSoonWithoutPractitioner,
  platformApprovedActiveTcClinicsCount,
  buildClinicMapByPartner,
  derivePartnerTcStatus,
  isClinicListedForTeleconsultationPlatform,
} from "../../../utils/teleconsultationAdminMetrics";

const TeleconsultationOperationsDashboard = () => {
  const history = useHistory();
  const [allClinics, setAllClinics] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [tcEnabled, setTcEnabled] = useState(true);
  const [savingTcEnabled, setSavingTcEnabled] = useState(false);
  const [slotBooking, setSlotBooking] = useState({
    bufferMinutes: 5,
    minDelayHours: 2,
    maxBookingDays: 60,
    horizonDays: 60,
  });
  const [reminderTiming, setReminderTiming] = useState({
    earlyMinutes: 1440,
    lateMinutes: 15,
  });
  const [savingSlotBooking, setSavingSlotBooking] = useState(false);
  const [savingReminderTiming, setSavingReminderTiming] = useState(false);
  const [practitionersByClinic, setPractitionersByClinic] = useState({});
  const [loadingPractitionersByClinic, setLoadingPractitionersByClinic] = useState({});
  const [error, setError] = useState("");
  const [clinicsWithNoPractitioner, setClinicsWithNoPractitioner] = useState([]);

  const fetchTcSettingsAndData = useCallback((options = {}) => {
    const { signal } = options;
    http
      .get("/api/v1/settings", { signal })
      .then((res) => {
        const list = res.data?.data || [];
        const tcSetting = Array.isArray(list) ? list.find((s) => s?.key === "teleconsultation_enabled") : null;
        if (!tcSetting) {
          setTcEnabled(true);
        } else {
          const v = tcSetting.value;
          setTcEnabled(v === true || v === "true" || v === 1 || v === "1");
        }
        const num = (key, fallback) => {
          const row = Array.isArray(list) ? list.find((s) => s?.key === key) : null;
          const n = Number(row?.value);
          return Number.isFinite(n) ? n : fallback;
        };
        setSlotBooking({
          bufferMinutes: num("tc_buffer_minutes", 5),
          minDelayHours: num("tc_min_booking_delay_hours", 2),
          maxBookingDays: num("tc_max_booking_days", 60),
          horizonDays: num("tc_slot_horizon_days", 60),
        });
        const legacyHours = num("tc_reminder_hours_before", 24);
        setReminderTiming({
          earlyMinutes: num("tc_reminder_early_minutes", legacyHours * 60 || 1440),
          lateMinutes: num("tc_reminder_late_minutes", 15),
        });
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setTcEnabled(true);
      });

    http
      .get("/api/v1/partners/medical/clinics", { signal })
      .then((res) => {
        const list = res.data?.data || res.data?.clinics || res.data || [];
        setAllClinics(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setAllClinics([]);
      });

    http
      .get("/api/v1/medical-partners", { signal })
      .then((res) => {
        const list = res.data?.data || res.data || [];
        setAllPartners(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setAllPartners([]);
      });

    http
      .get("/api/v1/consultations", { params: { limit: 500 }, signal })
      .then((res) => {
        const list = res.data?.data || res.data?.consultations || res.data || [];
        setConsultations(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setConsultations([]);
      });
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    setError("");
    fetchTcSettingsAndData({ signal: ac.signal });
    return () => ac.abort();
  }, [fetchTcSettingsAndData]);

  const tcClinics = useMemo(
    () => (Array.isArray(allClinics) ? allClinics.filter((c) => c?.offersTeleconsultation === true) : []),
    [allClinics]
  );

  const clinicMapByPartner = useMemo(() => buildClinicMapByPartner(tcClinics), [tcClinics]);

  const partnersWithTcStatus = useMemo(() => {
    const list = Array.isArray(allPartners) ? allPartners : [];
    return list.map((p) => ({
      ...p,
      tcStatusResolved: derivePartnerTcStatus(p, clinicMapByPartner),
      tcClinics: clinicMapByPartner.get(String(p?._id || "")) || [],
    }));
  }, [allPartners, clinicMapByPartner]);

  const pendingPartnerRequests = useMemo(
    () => partnersWithTcStatus.filter((p) => p.tcStatusResolved === "pending"),
    [partnersWithTcStatus]
  );

  const approvedPartners = useMemo(
    () => partnersWithTcStatus.filter((p) => p.tcStatusResolved === "approved"),
    [partnersWithTcStatus]
  );

  const platformApprovedTcClinicsCount = useMemo(
    () => platformApprovedActiveTcClinicsCount(tcClinics),
    [tcClinics]
  );

  const partnersPendingTooLong = useMemo(
    () => selectPartnersPendingTooLong(pendingPartnerRequests),
    [pendingPartnerRequests]
  );

  const paidSoonWithoutPractitioner = useMemo(
    () => selectPaidSoonWithoutPractitioner(consultations),
    [consultations]
  );

  useEffect(() => {
    const targets = tcClinics.filter(
      (c) =>
        String(c?.status || "").toLowerCase() === "active" &&
        isClinicListedForTeleconsultationPlatform(c) &&
        c?.offersTeleconsultation === true
    );
    if (targets.length === 0) {
      setClinicsWithNoPractitioner([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const empty = [];
      await Promise.all(
        targets.map(async (c) => {
          try {
            const res = await http.get(`/api/v1/partners/medical/clinics/${c._id}/practitioners`);
            const arr = res.data?.data || [];
            const doctors = arr.filter((p) => !["nurse", "assistant"].includes(String(p?.role || "").toLowerCase()));
            if (doctors.length === 0) empty.push({ _id: c._id, name: c.name || "Clinic" });
          } catch {
            /* ignore */
          }
        })
      );
      if (!cancelled) setClinicsWithNoPractitioner(empty);
    })();
    return () => {
      cancelled = true;
    };
  }, [tcClinics]);

  const saveReminderTiming = async () => {
    if (reminderTiming.lateMinutes >= reminderTiming.earlyMinutes) {
      setError("Short reminder must be fewer minutes before the visit than the early reminder.");
      return;
    }
    setSavingReminderTiming(true);
    setError("");
    try {
      await http.put("/api/v1/settings/bulk", {
        settings: [
          {
            key: "tc_reminder_early_minutes",
            value: reminderTiming.earlyMinutes,
            label: "Video consultation early reminder (minutes before)",
            category: "feature",
            type: "number",
          },
          {
            key: "tc_reminder_late_minutes",
            value: reminderTiming.lateMinutes,
            label: "Video consultation short reminder (minutes before)",
            category: "feature",
            type: "number",
          },
        ],
      });
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save reminder timing");
    } finally {
      setSavingReminderTiming(false);
    }
  };

  const saveSlotBookingDefaults = async () => {
    if (slotBooking.horizonDays < slotBooking.maxBookingDays) {
      setError("Slot generation horizon must be at least the max booking window (days).");
      return;
    }
    setSavingSlotBooking(true);
    setError("");
    try {
      await http.put("/api/v1/settings/bulk", {
        settings: [
          {
            key: "tc_buffer_minutes",
            value: slotBooking.bufferMinutes,
            label: "TC buffer between slots (minutes)",
            category: "feature",
            type: "number",
          },
          {
            key: "tc_min_booking_delay_hours",
            value: slotBooking.minDelayHours,
            label: "TC minimum booking delay (hours)",
            category: "feature",
            type: "number",
          },
          {
            key: "tc_max_booking_days",
            value: slotBooking.maxBookingDays,
            label: "TC max booking window (days)",
            category: "feature",
            type: "number",
          },
          {
            key: "tc_slot_horizon_days",
            value: slotBooking.horizonDays,
            label: "TC slot generation horizon (days)",
            category: "feature",
            type: "number",
          },
        ],
      });
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save slot booking defaults");
    } finally {
      setSavingSlotBooking(false);
    }
  };

  const saveGlobalTcEnabled = async (nextValue) => {
    setSavingTcEnabled(true);
    try {
      await http.put("/api/v1/settings/bulk", {
        settings: [
          {
            key: "teleconsultation_enabled",
            value: nextValue,
            label: "Teleconsultation globally enabled",
            description: "Enable/disable teleconsultation flow globally",
            category: "feature",
            type: "boolean",
            isPublic: true,
          },
        ],
      });
      setTcEnabled(nextValue);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save teleconsultation global setting");
    } finally {
      setSavingTcEnabled(false);
    }
  };

  const loadPractitioners = async (clinicId) => {
    if (!clinicId || loadingPractitionersByClinic[clinicId]) return;
    setLoadingPractitionersByClinic((prev) => ({ ...prev, [clinicId]: true }));
    try {
      const res = await http.get(`/api/v1/partners/medical/clinics/${clinicId}/practitioners`);
      setPractitionersByClinic((prev) => ({ ...prev, [clinicId]: res.data?.data || [] }));
    } catch (e) {
      setPractitionersByClinic((prev) => ({ ...prev, [clinicId]: [] }));
      setError(e.response?.data?.message || "Failed to load clinic practitioners");
    } finally {
      setLoadingPractitionersByClinic((prev) => ({ ...prev, [clinicId]: false }));
    }
  };

  return (
    <>
      <TeleconsultationPageShell
        headTitle="Pilotage"
        title="Pilotage"
        description="Operational settings, alerts, and shortcuts. Catalog listing decisions live in Catalogue go-live."
        aside={
          <>
            <Button
              color="primary"
              outline
              className="mr-2 mb-2"
              onClick={() => history.push(getPublicUrl("teleconsultations/practitioner-approvals"))}
            >
              Catalogue go-live
            </Button>
            <Button
              color="light"
              className="mr-2 mb-2"
              onClick={() => history.push(getPublicUrl("teleconsultations/stats"))}
            >
              Statistics
            </Button>
            <Button color="light" className="mr-2 mb-2" onClick={() => fetchTcSettingsAndData()}>
              <Icon name="refresh" />
              Refresh
            </Button>
          </>
        }
      >
        <Block>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <Row className="mb-3">
            <Col md="12">
              <h6 className="overline-title text-soft mb-2">Business alerts</h6>
            </Col>
            <Col md="6" lg="4" className="mb-3">
              <div className={`card card-bordered h-100 ${clinicsWithNoPractitioner.length ? "border-warning" : ""}`}>
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Approved TC clinics without a practitioner</h6>
                  <h4 className="mb-0">{clinicsWithNoPractitioner.length}</h4>
                  <p className="small text-soft mt-2 mb-0">
                    Active, platform TC–approved clinics offering TC but no doctor row on file.
                  </p>
                  {clinicsWithNoPractitioner.length > 0 && (
                    <ul className="small mb-0 mt-2 ps-3">
                      {clinicsWithNoPractitioner.slice(0, 8).map((c) => (
                        <li key={c._id}>{c.name}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Col>
            <Col md="6" lg="4">
              <div className={`card card-bordered h-100 ${paidSoonWithoutPractitioner.length ? "border-warning" : ""}`}>
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Paid consultations (next 24h) — no practitioner</h6>
                  <h4 className="mb-0">{paidSoonWithoutPractitioner.length}</h4>
                  <p className="small text-soft mt-2 mb-0">Status paid, scheduled within 24h, practitioner not assigned.</p>
                </div>
              </div>
            </Col>
            <Col md="6" lg="4" className="mb-3">
              <div className={`card card-bordered h-100 ${partnersPendingTooLong.length ? "border-warning" : ""}`}>
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Partners pending TC (more than {TC_ADMIN_PENDING_STALE_DAYS} days)</h6>
                  <h4 className="mb-0">{partnersPendingTooLong.length}</h4>
                  <p className="small text-soft mt-2 mb-0">Still pending with no update in {TC_ADMIN_PENDING_STALE_DAYS}+ days (or missing dates).</p>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md="6" lg="3" className="mb-3">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Global teleconsultation</h6>
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="small text-soft">{tcEnabled ? "Enabled" : "Disabled"}</span>
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="tcGlobalEnabledOps"
                        checked={tcEnabled}
                        disabled={savingTcEnabled}
                        onChange={(e) => saveGlobalTcEnabled(e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor="tcGlobalEnabledOps">
                        Toggle
                      </label>
                    </div>
                  </div>
                  <p className="small text-soft mt-2 mb-0">Controls patient teleconsultation visibility globally.</p>
                </div>
              </div>
            </Col>
            <Col md="6" lg="4" className="mb-3">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Pending partner requests</h6>
                  <h4 className="mb-0">{pendingPartnerRequests.length}</h4>
                  <p className="small text-soft mt-2 mb-0">Partner-level TC requests waiting for admin decision.</p>
                </div>
              </div>
            </Col>
            <Col md="6" lg="3">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Approved partners (TC)</h6>
                  <h4 className="mb-0">{approvedPartners.length}</h4>
                  <p className="small text-soft mt-2 mb-0">Approval status is independent from clinic statuses.</p>
                </div>
              </div>
            </Col>
            <Col md="6" lg="3" className="mb-3">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <h6 className="overline-title mb-2">Active TC clinics (platform-approved)</h6>
                  <h4 className="mb-0">{platformApprovedTcClinicsCount}</h4>
                  <p className="small text-soft mt-2 mb-0">Offer TC, active listing, platform TC approval enabled.</p>
                </div>
              </div>
            </Col>
          </Row>

          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <h6 className="overline-title mb-2">Global slot booking defaults</h6>
              <p className="small text-soft mb-3">
                Platform-wide video consultation scheduling. Clinics can override individual fields. Saving triggers slot
                regeneration.
              </p>
              <Row className="g-gs">
                <Col sm="6" md="3">
                  <label className="form-label small" htmlFor="tc-slot-buffer">
                    Buffer (minutes)
                  </label>
                  <input
                    id="tc-slot-buffer"
                    type="number"
                    min={0}
                    max={180}
                    className="form-control"
                    value={slotBooking.bufferMinutes}
                    onChange={(e) =>
                      setSlotBooking((prev) => ({
                        ...prev,
                        bufferMinutes: Math.min(180, Math.max(0, Number(e.target.value) || 0)),
                      }))
                    }
                  />
                </Col>
                <Col sm="6" md="3">
                  <label className="form-label small" htmlFor="tc-slot-min-delay">
                    Min delay (hours)
                  </label>
                  <input
                    id="tc-slot-min-delay"
                    type="number"
                    min={0}
                    max={168}
                    className="form-control"
                    value={slotBooking.minDelayHours}
                    onChange={(e) =>
                      setSlotBooking((prev) => ({
                        ...prev,
                        minDelayHours: Math.min(168, Math.max(0, Number(e.target.value) || 0)),
                      }))
                    }
                  />
                </Col>
                <Col sm="6" md="3">
                  <label className="form-label small" htmlFor="tc-slot-max-days">
                    Max booking window (days)
                  </label>
                  <input
                    id="tc-slot-max-days"
                    type="number"
                    min={1}
                    max={120}
                    className="form-control"
                    value={slotBooking.maxBookingDays}
                    onChange={(e) =>
                      setSlotBooking((prev) => ({
                        ...prev,
                        maxBookingDays: Math.min(120, Math.max(1, Number(e.target.value) || 1)),
                      }))
                    }
                  />
                </Col>
                <Col sm="6" md="3">
                  <label className="form-label small" htmlFor="tc-slot-horizon">
                    Generation horizon (days)
                  </label>
                  <input
                    id="tc-slot-horizon"
                    type="number"
                    min={1}
                    max={120}
                    className="form-control"
                    value={slotBooking.horizonDays}
                    onChange={(e) =>
                      setSlotBooking((prev) => ({
                        ...prev,
                        horizonDays: Math.min(120, Math.max(1, Number(e.target.value) || 1)),
                      }))
                    }
                  />
                </Col>
              </Row>
              {slotBooking.horizonDays < slotBooking.maxBookingDays ? (
                <p className="small text-danger mt-2 mb-0">
                  Horizon must be at least {slotBooking.maxBookingDays} days (max booking window).
                </p>
              ) : null}
              <Button
                color="primary"
                size="sm"
                className="mt-3"
                disabled={savingSlotBooking || slotBooking.horizonDays < slotBooking.maxBookingDays}
                onClick={saveSlotBookingDefaults}
              >
                {savingSlotBooking ? "Saving…" : "Save defaults"}
              </Button>
            </div>
          </div>

          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <h6 className="overline-title mb-2">Patient reminder emails</h6>
              <p className="small text-soft mb-3">
                Two reminders per paid visit: an early heads-up and a short reminder before the slot.
                The server cron scans every 5 minutes (fixed cadence). Same-day bookings only receive the
                short reminder when the early window has already passed.
              </p>
              <Row className="g-gs">
                <Col sm="6" md="4">
                  <label className="form-label small" htmlFor="tc-reminder-early">
                    Early reminder (minutes before)
                  </label>
                  <input
                    id="tc-reminder-early"
                    type="number"
                    min={30}
                    max={4320}
                    className="form-control"
                    value={reminderTiming.earlyMinutes}
                    onChange={(e) =>
                      setReminderTiming((prev) => ({
                        ...prev,
                        earlyMinutes: Math.min(4320, Math.max(30, Number(e.target.value) || 30)),
                      }))
                    }
                  />
                </Col>
                <Col sm="6" md="4">
                  <label className="form-label small" htmlFor="tc-reminder-late">
                    Short reminder (minutes before)
                  </label>
                  <input
                    id="tc-reminder-late"
                    type="number"
                    min={5}
                    max={180}
                    className="form-control"
                    value={reminderTiming.lateMinutes}
                    onChange={(e) =>
                      setReminderTiming((prev) => ({
                        ...prev,
                        lateMinutes: Math.min(180, Math.max(5, Number(e.target.value) || 5)),
                      }))
                    }
                  />
                </Col>
              </Row>
              {reminderTiming.lateMinutes >= reminderTiming.earlyMinutes ? (
                <p className="small text-danger mt-2 mb-0">
                  Short reminder must be less than the early reminder (in minutes).
                </p>
              ) : null}
              <Button
                color="primary"
                size="sm"
                className="mt-3"
                disabled={savingReminderTiming || reminderTiming.lateMinutes >= reminderTiming.earlyMinutes}
                onClick={saveReminderTiming}
              >
                {savingReminderTiming ? "Saving…" : "Save reminder timing"}
              </Button>
            </div>
          </div>

          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Partner teleconsultation requests</h6>
                <div className="d-flex flex-wrap">
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    className="mr-2"
                    onClick={() => history.push(getPublicUrl("teleconsultations/practitioner-approvals"))}
                  >
                    Catalogue go-live
                  </Button>
                  <Button size="sm" color="light" onClick={() => fetchTcSettingsAndData()}>
                    Refresh
                  </Button>
                </div>
              </div>
              <p className="small text-soft mb-3">
                Read-only queue. Approve partner program and catalog listing in Catalogue go-live.
              </p>
              {pendingPartnerRequests.length === 0 ? (
                <p className="small text-soft mb-0">No pending partner-level requests.</p>
              ) : (
                pendingPartnerRequests.map((partner) => (
                  <div key={partner._id} className="border rounded p-2 mb-2">
                    <div className="d-flex flex-wrap align-items-center justify-content-between">
                      <div className="mr-2 mb-2">
                        <strong>{partner.organization_name || "Partner"}</strong>
                        <div className="small text-soft">
                          Country: {partner.contact_person_country || "—"} · TC clinics: {partner.tcClinics.length}
                        </div>
                      </div>
                      <div className="d-flex flex-wrap align-items-center">
                        <Button
                          size="sm"
                          color="primary"
                          className="mr-2 mb-2"
                          onClick={() => history.push(getPublicUrl("teleconsultations/practitioner-approvals"))}
                        >
                          Catalogue go-live
                        </Button>
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          className="mb-2"
                          onClick={() => history.push(getPublicUrl(`medical-partner-details/${partner._id}`))}
                        >
                          Open partner
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <h6 className="mb-2">Approved partners and clinics</h6>
              {approvedPartners.length === 0 ? (
                <p className="small text-soft mb-0">No approved partner currently active in teleconsultation.</p>
              ) : (
                approvedPartners.map((partner) => (
                  <div key={partner._id} className="mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <strong>{partner.organization_name || "Partner"}</strong>
                      <span className="badge badge-dim bg-primary">{partner.tcClinics.length} clinic(s)</span>
                    </div>
                    <div className="mt-2">
                      {partner.tcClinics.map((clinic) => (
                        <div key={clinic._id} className="border rounded p-3 mb-2">
                          <div className="d-flex flex-wrap align-items-start justify-content-between">
                            <div className="flex-grow-1 min-w-0 pr-2 mr-2 mb-2">
                              <strong className="d-block">{clinic.name || "Clinic"}</strong>
                              <div className="small text-soft mt-1 mb-0 lh-base">
                                Status: {clinic.status || "—"} · Country: {clinic.address?.country || "—"}
                              </div>
                            </div>
                            <div className="d-flex flex-wrap flex-shrink-0 align-items-start">
                              <Button size="sm" color="light" className="mr-2 mb-2" onClick={() => loadPractitioners(clinic._id)}>
                                {loadingPractitionersByClinic[clinic._id] ? "Loading..." : "Doctors list"}
                              </Button>
                              <Button
                                size="sm"
                                color="primary"
                                outline
                                className="mb-2"
                                onClick={() => history.push(getPublicUrl(`clinics/${clinic._id}/prices`))}
                              >
                                Edit clinic
                              </Button>
                            </div>
                          </div>
                          {Array.isArray(practitionersByClinic[clinic._id]) && (
                            <div className="mt-3 pt-2 border-top">
                              {practitionersByClinic[clinic._id].length === 0 ? (
                                <p className="small text-soft mb-0 lh-base">No practitioner configured for this clinic.</p>
                              ) : (
                                <ul className="small mb-0 ps-3">
                                  {practitionersByClinic[clinic._id].map((p) => (
                                    <li key={p._id} className="mb-1">
                                      {p.displayName || p.user?.email || p.user?._id || "Practitioner"} · {p.role || "doctor"}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Block>
      </TeleconsultationPageShell>
    </>
  );
};

export default TeleconsultationOperationsDashboard;
